import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { motion } from "framer-motion";
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  Shield, 
  Sparkles,
  ArrowRight 
} from "lucide-react";

export default function GoogleCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const expiresIn = params.get("expires_in");
    const tokenType = params.get("token_type");

    console.log("Google Callback - Tokens received:", {
      accessToken: accessToken ? `${accessToken.substring(0, 20)}...` : null,
      refreshToken: refreshToken ? "present" : null,
      expiresIn,
      tokenType
    });

    if (!accessToken) {
      console.error("❌ No Token found on query param");
      const error = params.get("error");
      const errorDescription = params.get("error_description");
      setErrorMessage(errorDescription || error || "Authentication failed");
      setStatus("error");
      
      // Redirect after 3 seconds on error
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 3000);
      return;
    }

    // Store tokens
    try {
      localStorage.setItem("access_token", accessToken);
      
      if (refreshToken) {
        localStorage.setItem("refresh_token", refreshToken);
      }
      
      if (expiresIn) {
        const expiresAt = Date.now() + (parseInt(expiresIn) * 1000);
        localStorage.setItem("token_expires_at", expiresAt.toString());
      }

      console.log("✅ Tokens stored in localStorage");
      window.dispatchEvent(new Event('auth-storage-changed'));
      
      setStatus("success");
      
      // Redirect after 2 seconds on success
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 2000);
    } catch (err) {
      setErrorMessage("Failed to store authentication data");
      setStatus("error");
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 3000);
    }
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
        <div className="absolute bottom-0 -right-4 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-pulse animation-delay-4000"></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: Math.random() * 0.5,
            }}
            animate={{
              y: [null, -30, 30, -30],
              opacity: [null, 0, 0.5, 0],
            }}
            transition={{
              duration: Math.random() * 5 + 3,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        ))}
      </div>

      {/* Main Card */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", duration: 0.6 }}
        className="relative z-10 max-w-md w-full"
      >
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8">
          {/* Logo/Icon Section */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="flex justify-center mb-6"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full blur-xl opacity-70"></div>
              <div className="relative bg-gradient-to-br from-purple-500 to-pink-500 rounded-full p-4">
                <Shield className="w-12 h-12 text-white" />
              </div>
            </div>
          </motion.div>

          {/* Status Content */}
          <div className="text-center space-y-4">
            {status === "loading" && (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="inline-flex"
                >
                  <Loader2 className="w-16 h-16 text-purple-400" />
                </motion.div>
                <h2 className="text-2xl font-bold text-white">Completing Login</h2>
                <p className="text-purple-200">Verifying your credentials...</p>
                <div className="flex justify-center gap-1 mt-4">
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-2 h-2 bg-purple-400 rounded-full"
                      animate={{ scale: [1, 1.5, 1] }}
                      transition={{ delay: i * 0.2, duration: 1, repeat: Infinity }}
                    />
                  ))}
                </div>
              </>
            )}

            {status === "success" && (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", bounce: 0.5 }}
                >
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto" />
                </motion.div>
                <motion.h2
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="text-2xl font-bold text-white"
                >
                  Welcome Back!
                </motion.h2>
                <motion.p
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-green-200"
                >
                  Successfully authenticated
                </motion.p>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex items-center justify-center gap-2 text-purple-200 text-sm mt-4"
                >
                  <span>Redirecting to dashboard</span>
                  <ArrowRight className="w-4 h-4 animate-pulse" />
                </motion.div>
              </>
            )}

            {status === "error" && (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", bounce: 0.5 }}
                >
                  <XCircle className="w-16 h-16 text-red-400 mx-auto" />
                </motion.div>
                <h2 className="text-2xl font-bold text-white">Authentication Failed</h2>
                <p className="text-red-200">{errorMessage}</p>
                <p className="text-gray-300 text-sm mt-4">Redirecting to login page...</p>
              </>
            )}
          </div>

          {/* Animated Progress Bar for Loading */}
          {status === "loading" && (
            <motion.div
              className="mt-8 h-1 bg-purple-500/30 rounded-full overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <motion.div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 2, ease: "linear" }}
              />
            </motion.div>
          )}

          {/* Decorative Elements */}
          <div className="absolute -top-2 -right-2">
            <Sparkles className="w-6 h-6 text-yellow-400 animate-pulse" />
          </div>
        </div>

        {/* Security Badge */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-6 text-white/50 text-sm flex items-center justify-center gap-2"
        >
          <Shield className="w-4 h-4" />
          <span>Secure connection • OAuth 2.0</span>
        </motion.div>
      </motion.div>

      <style >{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
}

// http://localhost:5173/auth/google/callback?access_token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJ1c2VyX2lkIjoibXVoYW1tYWRoc2hhYW4iLCJ1c2VyX2VtYWlsIjoibXVoYW1tYWRoc2hhYW5AZ21haWwuY29tIiwidGVuYW50X2lkIjoidGVuYW50X2dsb2JhbCIsImFwcF9pZCI6bnVsbCwicm9sZXMiOlt7InJvbGVfaWQiOiJyb2xlX3N1cGVyX2FkbWluIiwicm9sZV9uYW1lIjoiU3VwZXIgQWRtaW4iLCJyb2xlX3Njb3BlIjoiZ2xvYmFsIiwic2NvcGVfdHlwZSI6Imdsb2JhbCIsInNjb3BlX2lkIjpudWxsLCJwZXJtaXNzaW9ucyI6WyIqIl19XSwiaXNfc3VwZXIiOnRydWUsImV4cCI6MTc3MzQxNjA4MSwiaWF0IjoxNzczMzk0NDgxfQ.BeJfEVLhFx1SCxVzLU-QxCUU14S28KvCfYTsF0cYLD4&refresh_token=55ebd761-80a3-4dc6-836a-e41af2e96c70&expires_in=3600&token_type=Bearer

// https://www.raashidh.com/auth/google/callback?
// state=03c025f19a6c14a45f91ca8e52fe3fbd5c49c419310455bb
// &code=4%2F0ATX87lM1BbQEodaza6zFgM8O5ikk7vZvg0qxORvoLxPlGZDamcMbmfQZRFL0UZfhH0yn9A
// &scope=email+profile+https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fuserinfo.profile+https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fuserinfo.email+openid&authuser=0&prompt=consent


// http://localhost:8787/api/auth/callback?state=23a2c36cb1eb78ca26d1a1099bb2b55a11956ebcd4d0eb2c&code=4%2F0ATX87lMGzCMN-xcUkLj-VCeSbqZwEwe8E-yhrfO69yTNRNuhmAV6ODfdaYVUgCvhrTN-VQ&scope=email+profile+https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fuserinfo.profile+https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fuserinfo.email+openid&authuser=0&prompt=consent